import { useMemo, useEffect, useRef, useState } from "react";
import { ArrowLeft, CalendarCheck, Bot } from "lucide-react";
import { ToolCard } from "./ToolCard";
import { BeforeAfter } from "./BeforeAfter";
import { RoiCalculator } from "./RoiCalculator";
import { SetupGuides } from "./SetupGuides";
import { PAIN_POINTS, TOOL_RECOMMENDATIONS } from "../data/automationData";

export const ResultsView = ({ businessType, selectedPains, onBack, onBook, onSaveAssessment }) => {
  const ref = useRef(null);
  const [autonomousOnly, setAutonomousOnly] = useState(false);

  const allTools = useMemo(() => {
    // Todas las herramientas de los dolores elegidos (sin duplicados)
    const collected = [];
    selectedPains.forEach((pid) => {
      (TOOL_RECOMMENDATIONS[pid] || []).forEach((t) => {
        if (!collected.find((c) => c.name === t.name)) collected.push(t);
      });
    });
    return collected;
  }, [selectedPains]);

  const tools = useMemo(
    () => (autonomousOnly ? allTools.filter((t) => t.autonomous) : allTools),
    [allTools, autonomousOnly]
  );

  const autonomousCount = useMemo(() => allTools.filter((t) => t.autonomous).length, [allTools]);

  const toolsCost = useMemo(
    () =>
      // stack más económico: la mejor herramienta por dolor
      selectedPains.reduce((sum, pid) => {
        const t = (TOOL_RECOMMENDATIONS[pid] || [])[0];
        return sum + (t?.monthlyCost || 0);
      }, 0),
    [selectedPains]
  );

  const suggestedHours = useMemo(() => {
    const dailyMinsSaved = PAIN_POINTS.filter((p) => selectedPains.includes(p.id)).reduce(
      (sum, p) => sum + (p.manualMinutes - p.aiMinutes),
      0
    );
    return (dailyMinsSaved * 6) / 60; // 6 working days/week
  }, [selectedPains]);

  useEffect(() => {
    ref.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  return (
    <section
      ref={ref}
      className="max-w-6xl mx-auto px-4 sm:px-6 py-16 md:py-20 scroll-mt-20"
      data-testid="results-section"
    >
      <button
        onClick={onBack}
        data-testid="results-back-btn"
        className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-primary transition-colors mb-8"
      >
        <ArrowLeft className="h-4 w-4" /> Cambiar mis respuestas
      </button>

      <div className="mb-12">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-accent mb-3">
          Tu plan personalizado
        </p>
        <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight max-w-2xl">
          Así puedes recuperar{" "}
          <span className="text-accent">{Math.round(suggestedHours)} horas</span> a la semana.
        </h2>
        <p className="text-muted-foreground mt-3 max-w-xl">
          Elegimos herramientas simples y probadas para tu negocio. Empieza por una — notarás la
          diferencia esta misma semana.
        </p>
      </div>

      {/* Herramientas recomendadas */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-5">
        <div>
          <h3 className="font-heading text-xl font-semibold">Recomendado para ti</h3>
          <p className="text-sm text-muted-foreground mt-1">
            {tools.length} herramientas · {autonomousCount} funcionan solas con IA
          </p>
        </div>
        <button
          onClick={() => setAutonomousOnly((v) => !v)}
          data-testid="autonomous-filter-toggle"
          aria-pressed={autonomousOnly}
          className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold border-2 transition-colors ${
            autonomousOnly
              ? "bg-accent text-accent-foreground border-accent"
              : "bg-card text-foreground border-border hover:border-accent/40"
          }`}
        >
          <Bot className="h-4 w-4" />
          {autonomousOnly ? "Mostrando solo autónomas" : "Solo herramientas autónomas"}
        </button>
      </div>

      {tools.length === 0 ? (
        <div
          className="rounded-2xl border border-dashed border-border bg-card p-10 text-center mb-14"
          data-testid="no-tools-message"
        >
          <Bot className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground">
            No hay herramientas 100% autónomas para esta selección. Desactiva el filtro para ver
            todas las opciones.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-14">
          {tools.map((tool, i) => (
            <ToolCard key={tool.name} tool={tool} index={i} />
          ))}
        </div>
      )}

      {/* Before/After + ROI */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-14">
        <BeforeAfter painIds={selectedPains} />
        <RoiCalculator
          suggestedHours={suggestedHours}
          toolsCost={toolsCost}
          businessType={businessType}
          painPoints={selectedPains}
          onSave={onSaveAssessment}
        />
      </div>

      {/* Setup guides */}
      <div className="mb-14">
        <SetupGuides tools={tools} />
      </div>

      {/* CTA */}
      <div className="rounded-2xl bg-accent/10 border border-accent/20 p-8 md:p-10 text-center">
        <h3 className="font-heading text-2xl font-semibold mb-2">
          ¿Quieres que alguien lo configure contigo?
        </h3>
        <p className="text-muted-foreground max-w-md mx-auto mb-6">
          Agenda una llamada gratis de 15 minutos. Repasamos tu plan juntos — sin presión y sin
          tecnicismos.
        </p>
        <button
          onClick={onBook}
          data-testid="results-book-call-btn"
          className="inline-flex items-center gap-2 bg-accent text-accent-foreground px-8 py-4 rounded-full font-semibold hover:bg-accent/90 transition-colors"
        >
          <CalendarCheck className="h-5 w-5" /> Agenda mi llamada gratis
        </button>
      </div>
    </section>
  );
};
